/**
 * AgentConfigForm — structured form editor for agent YAML configs.
 *
 * Provides a tabbed form (Basic / Tools / Limits / Advanced YAML) as an
 * alternative to the raw YAML textarea. Bidirectional sync between form
 * state and the YAML string.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { Bot, Wrench, Settings, FileCode, Loader2 } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@/components/ui/tabs';
import type { AvailableResources } from '@/services/agentWizardApi';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface FormState {
  name: string;
  model: string;
  type: string;
  description: string;
  prompt: string;
  tools: string[];
  mcp_servers: string[];
  memory_enabled: boolean;
  memory_category: string;
  max_tokens_per_run: string;
  max_tool_calls_per_run: string;
  sync_mode: boolean;
  execution_timeout_seconds: string;
}

interface AgentConfigFormProps {
  config: string;
  onChange: (config: string) => void;
  resources?: AvailableResources | null;
}

// ---------------------------------------------------------------------------
// YAML <-> Form conversion
// ---------------------------------------------------------------------------

function configToFormState(config: string): FormState {
  const get = (key: string): string => {
    const match = config.match(new RegExp(`^${key}:\\s*(.+)$`, 'm'));
    return match?.[1]?.trim().replace(/^["']|["']$/g, '') ?? '';
  };

  // Extract multiline prompt (simplified: first multiline block after `prompt: |`)
  let prompt = '';
  const promptMatch = config.match(/^prompt:\s*\|\n([\s\S]*?)(?=^\w|\n---)/m);
  if (promptMatch) {
    prompt = promptMatch[1]
      .split('\n')
      .map((l) => l.replace(/^ {2}/, ''))
      .join('\n')
      .trim();
  } else {
    prompt = get('prompt');
  }

  // Extract tool list
  const toolsMatch = config.match(/^tools:\s*\n((?:\s+-\s+.+\n?)*)/m);
  const tools = toolsMatch
    ? toolsMatch[1]
        .split('\n')
        .map((l) => l.replace(/^\s*-\s*/, '').trim())
        .filter(Boolean)
    : [];

  // Extract mcp_servers list
  const mcpMatch = config.match(/^mcp_servers:\s*\n((?:\s+-\s+.+\n?)*)/m);
  const mcp_servers = mcpMatch
    ? mcpMatch[1]
        .split('\n')
        .map((l) => l.replace(/^\s*-\s*/, '').trim())
        .filter(Boolean)
    : [];

  // Memory
  const memoryEnabled = /memory:\s*\n\s+enabled:\s*true/m.test(config);

  return {
    name: get('name'),
    model: get('model'),
    type: get('type') || 'agent',
    description: get('description'),
    prompt,
    tools,
    mcp_servers,
    memory_enabled: memoryEnabled,
    memory_category: get('memory_category') || 'general',
    max_tokens_per_run: get('max_tokens_per_run') || '100000',
    max_tool_calls_per_run: get('max_tool_calls_per_run') || '50',
    sync_mode: get('sync_mode') !== 'false',
    execution_timeout_seconds: get('execution_timeout_seconds') || '300',
  };
}

function formStateToConfig(state: FormState, originalConfig: string): string {
  // Preserve the body (everything below the second ---) from the original
  const parts = originalConfig.split(/^---$/m);
  const body = parts.length >= 3 ? parts.slice(2).join('---').trim() : '';

  let yaml = '---\n';
  yaml += `name: ${state.name || 'my-agent'}\n`;
  yaml += `model: ${state.model || 'openrouter:google/gemini-3-flash-preview'}\n`;
  yaml += `type: ${state.type || 'agent'}\n`;
  if (state.description) {
    yaml += `description: "${state.description}"\n`;
  }
  if (state.prompt) {
    yaml += 'prompt: |\n';
    state.prompt.split('\n').forEach((line) => {
      yaml += `  ${line}\n`;
    });
  }
  if (state.tools.length > 0) {
    yaml += 'tools:\n';
    state.tools.forEach((t) => {
      yaml += `  - ${t}\n`;
    });
  }
  if (state.mcp_servers.length > 0) {
    yaml += 'mcp_servers:\n';
    state.mcp_servers.forEach((s) => {
      yaml += `  - ${s}\n`;
    });
  }
  if (state.memory_enabled) {
    yaml += 'memory:\n  enabled: true\n';
    if (state.memory_category && state.memory_category !== 'general') {
      yaml += `memory_category: ${state.memory_category}\n`;
    }
  }
  yaml += 'limits:\n';
  yaml += `  max_tokens_per_run: ${state.max_tokens_per_run}\n`;
  yaml += `  max_tool_calls_per_run: ${state.max_tool_calls_per_run}\n`;
  if (!state.sync_mode) {
    yaml += 'sync_mode: false\n';
  }
  if (state.execution_timeout_seconds !== '300') {
    yaml += `execution_timeout_seconds: ${state.execution_timeout_seconds}\n`;
  }
  yaml += '---\n';

  if (body) {
    yaml += '\n' + body + '\n';
  }

  return yaml;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function AgentConfigForm({ config, onChange, resources }: AgentConfigFormProps) {
  const [form, setForm] = useState<FormState>(() => configToFormState(config));
  const [activeTab, setActiveTab] = useState('basic');

  // Sync form -> config when form changes (except when in YAML tab)
  const updateField = useCallback(
    (field: keyof FormState, value: any) => {
      setForm((prev) => {
        const next = { ...prev, [field]: value };
        if (activeTab !== 'yaml') {
          onChange(formStateToConfig(next, config));
        }
        return next;
      });
    },
    [activeTab, config, onChange],
  );

  // Sync config -> form when switching away from YAML tab
  useEffect(() => {
    if (activeTab !== 'yaml') {
      setForm(configToFormState(config));
    }
  }, [activeTab, config]);

  const toggleTool = (toolName: string) => {
    const tools = form.tools.includes(toolName)
      ? form.tools.filter((t) => t !== toolName)
      : [...form.tools, toolName];
    updateField('tools', tools);
  };

  const toggleMcpServer = (name: string) => {
    const servers = form.mcp_servers.includes(name)
      ? form.mcp_servers.filter((s) => s !== name)
      : [...form.mcp_servers, name];
    updateField('mcp_servers', servers);
  };

  const availableTools = resources?.tools ?? [
    { name: 'memory_save', description: 'Save data to memory' },
    { name: 'memory_search', description: 'Search saved memories' },
    { name: 'execute_python', description: 'Execute Python code' },
    { name: 'evaluate_expression', description: 'Evaluate math expressions' },
    { name: 'request_human_input', description: 'Request input from a human' },
  ];

  const availableMcpServers = resources?.mcp_servers ?? [];
  const availableModels = resources?.models ?? [
    { id: 'openrouter:google/gemini-3-flash-preview', name: 'Gemini 3 Flash', provider: 'Google' },
    { id: 'openrouter:google/gemini-3-pro-preview', name: 'Gemini 3 Pro', provider: 'Google' },
    { id: 'openrouter:anthropic/claude-sonnet-4.5', name: 'Claude Sonnet 4.5', provider: 'Anthropic' },
    { id: 'openrouter:anthropic/claude-opus-4.5', name: 'Claude Opus 4.5', provider: 'Anthropic' },
    { id: 'openrouter:openai/gpt-5.2', name: 'GPT-5.2', provider: 'OpenAI' },
  ];

  return (
    <Tabs value={activeTab} onValueChange={setActiveTab}>
      <TabsList className="grid w-full grid-cols-4">
        <TabsTrigger value="basic" className="gap-1.5">
          <Bot className="h-3.5 w-3.5" /> Basic
        </TabsTrigger>
        <TabsTrigger value="tools" className="gap-1.5">
          <Wrench className="h-3.5 w-3.5" /> Tools
        </TabsTrigger>
        <TabsTrigger value="limits" className="gap-1.5">
          <Settings className="h-3.5 w-3.5" /> Limits
        </TabsTrigger>
        <TabsTrigger value="yaml" className="gap-1.5">
          <FileCode className="h-3.5 w-3.5" /> YAML
        </TabsTrigger>
      </TabsList>

      {/* Basic Tab */}
      <TabsContent value="basic" className="space-y-4 mt-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="form-name">Agent Name</Label>
            <Input
              id="form-name"
              value={form.name}
              onChange={(e) => updateField('name', e.target.value)}
              placeholder="my-agent"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="form-model">Model</Label>
            <Select value={form.model} onValueChange={(v) => updateField('model', v)}>
              <SelectTrigger id="form-model">
                <SelectValue placeholder="Select model" />
              </SelectTrigger>
              <SelectContent>
                {availableModels.map((m) => (
                  <SelectItem key={m.id} value={m.id}>
                    {m.name} ({m.provider})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="form-type">Agent Type</Label>
            <Select value={form.type} onValueChange={(v) => updateField('type', v)}>
              <SelectTrigger id="form-type">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="agent">Simple Agent</SelectItem>
                <SelectItem value="graph">Multi-Agent Graph</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label htmlFor="form-desc">Description</Label>
            <Input
              id="form-desc"
              value={form.description}
              onChange={(e) => updateField('description', e.target.value)}
              placeholder="What does this agent do?"
            />
          </div>
        </div>

        <div className="space-y-2">
          <Label htmlFor="form-prompt">System Prompt</Label>
          <Textarea
            id="form-prompt"
            value={form.prompt}
            onChange={(e) => updateField('prompt', e.target.value)}
            rows={10}
            className="resize-y"
            placeholder="You are a helpful assistant..."
          />
          <p className="text-xs text-muted-foreground">
            {form.prompt.length} characters
          </p>
        </div>
      </TabsContent>

      {/* Tools & Capabilities Tab */}
      <TabsContent value="tools" className="space-y-6 mt-4">
        <div className="space-y-3">
          <Label className="text-sm font-medium">Built-in Tools</Label>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {availableTools.map((tool) => (
              <label
                key={tool.name}
                className="flex items-center gap-3 rounded-md border p-3 cursor-pointer hover:bg-muted/50 transition-colors"
              >
                <Switch
                  checked={form.tools.includes(tool.name)}
                  onCheckedChange={() => toggleTool(tool.name)}
                />
                <div className="min-w-0">
                  <span className="text-sm font-medium">{tool.name}</span>
                  {tool.description && (
                    <p className="text-xs text-muted-foreground truncate">{tool.description}</p>
                  )}
                </div>
              </label>
            ))}
          </div>
        </div>

        {availableMcpServers.length > 0 && (
          <div className="space-y-3">
            <Separator />
            <Label className="text-sm font-medium">MCP Servers</Label>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {availableMcpServers.map((srv) => (
                <label
                  key={srv.name}
                  className="flex items-center gap-3 rounded-md border p-3 cursor-pointer hover:bg-muted/50 transition-colors"
                >
                  <Switch
                    checked={form.mcp_servers.includes(srv.name)}
                    onCheckedChange={() => toggleMcpServer(srv.name)}
                  />
                  <div className="min-w-0">
                    <span className="text-sm font-medium">{srv.name}</span>
                    {srv.description && (
                      <p className="text-xs text-muted-foreground truncate">{srv.description}</p>
                    )}
                  </div>
                </label>
              ))}
            </div>
          </div>
        )}

        <div className="space-y-3">
          <Separator />
          <div className="flex items-center justify-between">
            <div>
              <Label className="text-sm font-medium">Memory</Label>
              <p className="text-xs text-muted-foreground">
                Save and recall information across runs
              </p>
            </div>
            <Switch
              checked={form.memory_enabled}
              onCheckedChange={(v) => updateField('memory_enabled', v)}
            />
          </div>
          {form.memory_enabled && (
            <div className="space-y-2 pl-4 border-l-2 border-muted">
              <Label htmlFor="form-mem-cat" className="text-sm">Memory Category</Label>
              <Input
                id="form-mem-cat"
                value={form.memory_category}
                onChange={(e) => updateField('memory_category', e.target.value)}
                placeholder="general"
                className="w-48"
              />
            </div>
          )}
        </div>

        <div className="flex flex-wrap gap-2">
          <span className="text-xs text-muted-foreground">Active:</span>
          {form.tools.map((t) => (
            <Badge key={t} variant="secondary" className="text-xs">{t}</Badge>
          ))}
          {form.mcp_servers.map((s) => (
            <Badge key={s} variant="outline" className="text-xs">MCP: {s}</Badge>
          ))}
          {form.tools.length === 0 && form.mcp_servers.length === 0 && (
            <span className="text-xs text-muted-foreground italic">No tools selected</span>
          )}
        </div>
      </TabsContent>

      {/* Limits Tab */}
      <TabsContent value="limits" className="space-y-4 mt-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="form-max-tokens">Max Tokens Per Run</Label>
            <Select
              value={form.max_tokens_per_run}
              onValueChange={(v) => updateField('max_tokens_per_run', v)}
            >
              <SelectTrigger id="form-max-tokens">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="30000">30,000 (Light)</SelectItem>
                <SelectItem value="60000">60,000 (Standard)</SelectItem>
                <SelectItem value="100000">100,000 (Extended)</SelectItem>
                <SelectItem value="200000">200,000 (Heavy)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label htmlFor="form-max-tools">Max Tool Calls Per Run</Label>
            <Select
              value={form.max_tool_calls_per_run}
              onValueChange={(v) => updateField('max_tool_calls_per_run', v)}
            >
              <SelectTrigger id="form-max-tools">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="10">10 (Minimal)</SelectItem>
                <SelectItem value="20">20 (Light)</SelectItem>
                <SelectItem value="50">50 (Standard)</SelectItem>
                <SelectItem value="100">100 (Heavy)</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="form-timeout">Execution Timeout</Label>
            <Select
              value={form.execution_timeout_seconds}
              onValueChange={(v) => updateField('execution_timeout_seconds', v)}
            >
              <SelectTrigger id="form-timeout">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="60">1 minute</SelectItem>
                <SelectItem value="300">5 minutes</SelectItem>
                <SelectItem value="900">15 minutes</SelectItem>
                <SelectItem value="3600">60 minutes</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-center justify-between rounded-md border p-3">
            <div>
              <Label className="text-sm font-medium">Sync Mode</Label>
              <p className="text-xs text-muted-foreground">
                Run synchronously (60s limit for Vercel)
              </p>
            </div>
            <Switch
              checked={form.sync_mode}
              onCheckedChange={(v) => updateField('sync_mode', v)}
            />
          </div>
        </div>
      </TabsContent>

      {/* Advanced YAML Tab */}
      <TabsContent value="yaml" className="space-y-2 mt-4">
        <Label className="text-sm font-medium">Raw Agent Configuration (YAML + Markdown)</Label>
        <Textarea
          value={config}
          onChange={(e) => onChange(e.target.value)}
          rows={20}
          className="font-mono text-sm resize-y"
        />
        <p className="text-xs text-muted-foreground">
          Edit the raw YAML frontmatter and markdown body directly. Changes here override form fields.
        </p>
      </TabsContent>
    </Tabs>
  );
}

export default AgentConfigForm;
